// CANONICAL COPY (GH #7115). src/utils/artefactEvidence.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

/**
 * src/utils/artefactEvidence.js
 *
 * One answer to "does this artefact actually carry a stored file?", for every
 * surface that decides something on it.
 *
 * Why this exists (ARTF-B-002, moved 2026-09-21)
 * ----------------------------------------------
 * `uploadArtefact()` in src/api/evidenceStorage.js was a stub that returned
 * null for as long as the artefact register has shipped: a file dropped onto
 * an artefact, a control or an obligation task was read for its name, size and
 * mime type and then discarded in the browser. The record was written with
 * `storagePath: null` and a real `filename`.
 *
 * Four independent readers took that `filename` string — supplied by the
 * client, never checked against storage — as proof a file was stored:
 *
 *   - evaluateControl() gate 4        → the control counted as evidenced
 *   - evidenceQuality.js              → the row scored as evidence
 *   - nodePrediction.isBlobBacked()   → the row was exempt from the decay
 *                                       clock, i.e. forecast fresh forever
 *   - ControlDetailModal              → the panel rendered "has a file"
 *
 * So a control's implementation-evidence gate passed on bytes that reached no
 * server. That is TRYGGR_INTENT.md §6.2 — the product telling a customer they
 * are compliant when they are not — and artefacts.json's own `mustNeverClaim`
 * ("that evidence exists ... when no artefact ... supports the claim"), live.
 *
 * The test is now an allow-list of what qualifies (a blob ref we could fetch)
 * rather than a deny-list of the one case known to be bad. A filename alone
 * proves nothing about storage and never did.
 *
 * Existing rows are NOT deleted — the title, description and control links are
 * real human judgement, and destroying them would destroy the record of a
 * decision. They stop counting as evidence and are surfaced as
 * `isFileMissing()` so the owner can re-upload the file.
 */

/**
 * True when the artefact's file is actually in storage and can be fetched.
 *
 * `blobName` is the legacy field from before `storagePath`; both are minted
 * server-side by an upload that succeeded, which is what makes them
 * trustworthy. `filename` is not — it is whatever the browser was holding.
 *
 * @param {{ storagePath?: string | null, blobName?: string | null } | null | undefined} artefact
 * @returns {boolean}
 */
function hasStoredFile(artefact) {
  return !!(artefact && (artefact.storagePath || artefact.blobName));
}

/**
 * True when the artefact claims a file that was never stored — the shape every
 * file-typed artefact created before real uploads landed has.
 *
 * Surfaced in the UI as "file missing — re-upload required". It is not an
 * error state to hide: it is the honest reading of a row that has been
 * counted as evidence and should not have been.
 *
 * @param {{ filename?: string | null, storagePath?: string | null, blobName?: string | null } | null | undefined} artefact
 * @returns {boolean}
 */
function isFileMissing(artefact) {
  return !!(artefact && artefact.filename && !hasStoredFile(artefact));
}

/**
 * Does this artefact count as evidence for a control?
 *
 * Two ways to qualify, and nothing else:
 *   1. a stored file, whatever the state of any external URL alongside it
 *      (ARTF-B-002's surviving decision — a lapsed supplementary link must
 *      never invalidate a document that is actually in storage);
 *   2. an external URL that is not flagged unverified (ARTF-B-001 — a human
 *      confirms currency; the sweeper may only flag).
 *
 * A row with neither — including one naming a file that was never stored — is
 * a record of intent, not evidence.
 *
 * @param {{ storagePath?: string | null, blobName?: string | null, externalUrl?: string | null, verificationStatus?: string | null } | null | undefined} artefact
 * @returns {boolean}
 */
function countsAsEvidence(artefact) {
  if (!artefact) return false;
  if (hasStoredFile(artefact)) return true;
  if (artefact.externalUrl) return artefact.verificationStatus !== 'unverified';
  return false;
}

module.exports = {
  hasStoredFile,
  isFileMissing,
  countsAsEvidence,
};
